<template>
    <Head :title="title">
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin />
        <link
            rel="stylesheet"
            href="https://fonts.googleapis.com/css2?family=Geist:wght@500;600;700&family=Inter:wght@400;500;600&family=JetBrains+Mono:wght@500&display=swap"
        />
    </Head>

    <PlayerShell
        ref="shellRef"
        :tracks="pair.tracks"
        :same-timeline="pair.sameTimeline"
        :initial-mode="initialMode"
        :locale="locale"
        :theme="rootStyle"
        :traffic-blocked="trafficBlocked"
        :unavailable="pair.tracks.length === 0"
        :label="showLabel"
        :branding="!!podcast.branding"
        :media-session="{ artist: podcast.title, album: podcast.title }"
        @player-event="analytics.handle"
    >
        <template #poster="{ play, track }">
            <PortalPoster slot="centered-chrome" accent-color :artwork="track.artworkUrl" :title="track.title" @play="play" />
        </template>
    </PlayerShell>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref, shallowRef } from "vue"
import { Head } from "@inertiajs/vue3"
import PlayerShell from "../PlayerCore/PlayerShell.vue"
import { useAudioWorker } from "../Player/useAudioWorker"
import { buildPodcastTracksV2 } from "../PlayerV2/podcastTracksV2.js"
import PortalPoster from "../PortalPlayer/Components/PortalPoster.vue"
import { createPodcastAnalytics } from "../VideoPlayer/podcastAnalytics.js"
import { themeVars } from "../VideoPlayer/theme.js"
import { useVideoAnalytics } from "../VideoPlayer/useVideoAnalytics.js"
import { provideVideoI18n } from "../VideoPlayer/useVideoI18n.js"
import { createViewSessionId } from "../VideoPlayer/viewSession.js"

/**
 * The "v2" player page — see PodcastControllerV2's own class docblock for the why. One page for
 * both audio and video: PlayerShell shows its native Watch/Listen toggle whenever the episode
 * has both tracks (see podcastTracksV2.js), and just plays audio with no toggle at all when it
 * doesn't. Props mirror VideoPlayer.vue's own shape — same PodcastData-derived episode/video
 * objects, same colors/theme contract — since PodcastControllerV2::playerV2() builds them the
 * same way PodcastController::videoPlayer() does.
 */
const props = defineProps({
    podcast: { type: Object, required: true },
    episode: { type: Object, required: true },
    video: { type: Object, default: null },
    videoSignature: { type: String, default: null },
    videoExpires: { type: Number, default: null },
    colors: { type: Object, default: null },
    theme: { type: String, default: "default" },
    preview: { type: Boolean, default: false },
    // Pro's preview signature (preview/signature/expires); null outside a signed preview.
    previewQuery: { type: Object, default: null },
    // The website's member signature (mexp/msig) for a members-only show; null otherwise.
    memberQuery: { type: Object, default: null },
    trafficBlocked: { type: Boolean, default: false },
    listenUrl: { type: String, required: true },
    listenVideoUrl: { type: String, required: true },
})

const { t, locale } = provideVideoI18n(props.podcast.locale)
const rootStyle = computed(() => themeVars(props.colors, props.theme))
const shellRef = ref(null)

const showLabel = computed(() =>
    [props.podcast.title, props.episode.episode > 0 ? t("Ep. {n}", { n: props.episode.episode }) : null].filter(Boolean).join(" · "),
)
const title = computed(() => `${props.episode?.title ?? ""} — ${props.podcast.title}`)

// One view session per page load, shared by the HLS URLs and the video beacon — same reasoning
// as VideoPlayer.vue's own.
const viewSession = createViewSessionId()

const masterUrl = props.video?.available
    ? route("podcast.video.master", {
          podcast: props.podcast.hash_id,
          episode: props.episode.hash_id,
          version: props.video.version,
          signature: props.videoSignature,
          expires: props.videoExpires,
          vs: viewSession,
          ...(props.preview ? { preview: "true" } : {}),
      })
    : null

const pair = buildPodcastTracksV2({
    podcast: props.podcast,
    episode: props.episode,
    video: props.video,
    masterUrl,
    transcriptUrl: (lang) =>
        route("podcast.transcription", {
            podcast: props.podcast.hash_id,
            episode: props.episode.hash_id,
            lang,
            ...(props.previewQuery ?? {}),
            ...(props.memberQuery ?? {}),
        }),
})

// Video first when there is one — matches v1's own default (the dedicated video route is what
// gets shared/embedded for a video episode today).
const initialMode = props.video?.available ? "video" : "audio"

const videoAnalytics = props.video?.available
    ? useVideoAnalytics({
          podcastHash: props.podcast.hash_id,
          episodeHash: props.episode.hash_id,
          duration: props.video.duration,
          logUrl: props.listenVideoUrl,
          preview: props.preview,
          viewSession,
      })
    : null

// Unlike VideoPlayer.vue (only wired when sameTimeline, since that page's whole point is video),
// v2 always has an audio track — see podcastTracksV2.js — so this is unconditional. useAudioWorker
// reads the <audio> element's duration, which the shell owns: it is handed over on mount.
const audioTimeline = shallowRef(null)
const audioWorker = useAudioWorker(audioTimeline, props.listenUrl, props.preview)

audioWorker.podcastId.value = props.podcast.hash_id
audioWorker.episodeId.value = props.episode.hash_id
audioWorker.episodeDuration.value = Math.ceil(props.episode.audio?.duration ?? 0)

const analytics = createPodcastAnalytics({ video: videoAnalytics, audio: audioWorker })

onMounted(() => {
    audioTimeline.value = shellRef.value?.audioElement() ?? null
})

// Lets whatever embeds this page (today: the Website Builder's Episode template, jumping to a
// chapter clicked in its own sidebar) seek the player without reaching into the iframe's DOM —
// cross-origin, so postMessage is the only option. Deliberately NOT the Portal Player's
// "springcast-portal" v1 contract (portalMessages.js) — that one is cross-repo-pinned to the
// website's learning portal and carries play/pause/mode too; this embed only needs seek, and
// keeping it separate means nothing here can drift that pinned contract. Open to any origin,
// same as a public video embed (YouTube, Vimeo) — this is a seek command, not privileged data.
function onWindowMessage(event) {
    const data = event.data

    if (!data || typeof data !== "object" || data.ns !== "springcast-player-v2" || data.type !== "seek") {
        return
    }

    if (typeof data.time !== "number" || !Number.isFinite(data.time)) {
        return
    }

    shellRef.value?.seek(data.time)
}

onMounted(() => {
    window.addEventListener("message", onWindowMessage)
})

onBeforeUnmount(() => {
    window.removeEventListener("message", onWindowMessage)
})
</script>
